import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'dompurify';

@Pipe({
  name: 'safeMarkdown',
  standalone: true,
  pure: true,
})
export class SafeMarkdownPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string | null | undefined): SafeHtml {
    if (!value?.trim()) {
      return '';
    }

    value = value
      .replace(/<start_of_turn>(system|user|model)/g, '')
      .replace(/<end_of_turn>/g, '')
      .trim();

    let html = this.escapeHtml(value);
    const codeBlocks: string[] = [];

    // Preserve fenced code blocks
    html = html.replace(
      /```([\w-]*)\n?([\s\S]*?)```/g,
      (_, language: string, code: string) => {
        const index = codeBlocks.length;

        codeBlocks.push(
          `<pre class="chat-code-block"><code class="language-${this.sanitizeLanguage(
            language,
          )}">${this.escapeHtml(code.trim())}</pre>`,
        );

        return `@@CODEBLOCK_${index}@@`;
      },
    );

    // Inline code
    html = html.replace(
      /`([^`\n]+)`/g,
      '<code class="chat-inline-code">$1</code>',
    );

    // Links — markdown-style [text](url)
    html = html.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>' +
      '<button class="chat-link-copy-btn d-inline-flex align-items-center justify-content-center bg-transparent border-0 cursor-pointer" data-copy-url="$2" title="Copy link" aria-label="Copy link">' +
      '<img src="images/copy-black.svg" width="12" height="12" alt="copy link" /></button>',
    );

    // Auto-link bare URLs — not already inside an href or data-copy-url attribute
    html = html.replace(
      /(?<!href=["']|data-copy-url=["'])(https?:\/\/[^\s<>"&)]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer">Click here</a>' +
      '<button class="chat-link-copy-btn d-inline-flex align-items-center justify-content-center bg-transparent border-0 cursor-pointer" data-copy-url="$1" title="Copy link" aria-label="Copy link">' +
      '<img src="images/copy-black.svg" width="12" height="12" alt="copy link" /></button>',
    );

    // Bold
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // Italic
    html = html.replace(
      /(^|[^*])\*(?!\s)(.+?)(?<!\s)\*(?!\*)/g,
      '$1<em>$2</em>',
    );

    // Headings
    html = html.replace(/^### (.*)$/gm, '<h4 class="chat-heading">$1</h4>');
    html = html.replace(/^## (.*)$/gm, '<h3 class="chat-heading">$1</h3>');
    html = html.replace(/^# (.*)$/gm, '<h2 class="chat-heading">$1</h2>');

    // Build HTML line by line
    const lines = html.split('\n');

    const output: string[] = [];

    let inOrderedList = false;
    let inUnorderedList = false;

    for (const rawLine of lines) {
      const line = rawLine.trim();

      if (!line) {
        if (inOrderedList) {
          output.push('</ol>');
          inOrderedList = false;
        }

        if (inUnorderedList) {
          output.push('</ul>');
          inUnorderedList = false;
        }

        continue;
      }

      // Capture the step number itself (group 1), not just detect that the
      // line is numbered.
      const orderedMatch = line.match(/^(\d+)\.\s+(.*)$/);

      if (orderedMatch) {
        if (inUnorderedList) {
          output.push('</ul>');
          inUnorderedList = false;
        }

        if (!inOrderedList) {
          // FIX: resume numbering from whatever step number the model
          // actually wrote (e.g. "4.") instead of always starting a fresh
          // <ol> at 1. Without this, any interruption between numbered
          // steps — a bullet sub-clarification under one step, or even a
          // plain explanatory line with no marker at all — closed the
          // current <ol> and opened a brand new one, and since a bare <ol>
          // always starts counting at 1, the user visibly saw the
          // numbering reset partway through a procedure (e.g.
          // 1, 2, 3, [bullets], 1, 2 instead of 1, 2, 3, [bullets], 4, 5).
          // Confirmed live against the chatbot backend: roughly half of
          // repeated "how do I …" procedural answers that included a
          // branch/clarification within one step hit this.
          const stepNumber = orderedMatch[1];
          output.push(
            stepNumber === '1' ? '<ol>' : `<ol start="${stepNumber}">`,
          );
          inOrderedList = true;
        }

        output.push(`<li>${orderedMatch[2]}</li>`);
        continue;
      }

      // FIX: accept "-", "*", or "+" as a bullet marker, not just "-".
      // "**bold**" has already been converted to <strong> above by this
      // point, so a leading "*" here is unambiguous — it can only be a
      // bullet, never emphasis syntax.
      const unorderedMatch = line.match(/^[-*+]\s+(.*)$/);

      if (unorderedMatch) {
        if (inOrderedList) {
          output.push('</ol>');
          inOrderedList = false;
        }

        if (!inUnorderedList) {
          output.push('<ul>');
          inUnorderedList = true;
        }

        output.push(`<li>${unorderedMatch[1]}</li>`);
        continue;
      }

      const isBlockLine =
        line.startsWith('<h') ||
        line.startsWith('<pre') ||
        line.startsWith('@@CODEBLOCK_');

      if ((inOrderedList || inUnorderedList) && !isBlockLine) {
        const lastIndex = output.length - 1;

        if (output[lastIndex]?.endsWith('</li>')) {
          output[lastIndex] =
            output[lastIndex].slice(0, -'</li>'.length) + `<br>${line}</li>`;
          continue;
        }
      }

      if (inOrderedList) {
        output.push('</ol>');
        inOrderedList = false;
      }

      if (inUnorderedList) {
        output.push('</ul>');
        inUnorderedList = false;
      }

      if (isBlockLine) {
        output.push(line);
      } else {
        output.push(`<p>${line}</p>`);
      }
    }

    if (inOrderedList) {
      output.push('</ol>');
    }

    if (inUnorderedList) {
      output.push('</ul>');
    }

    html = output.join('');

    // Restore code blocks
    codeBlocks.forEach((block, index) => {
      html = html.replace(`@@CODEBLOCK_${index}@@`, block);
    });

    const sanitizedHtml = DOMPurify.sanitize(html, { ADD_ATTR: ['data-copy-url'] });

    return this.sanitizer.bypassSecurityTrustHtml(sanitizedHtml);
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private sanitizeLanguage(language: string): string {
    return (language || 'text').replace(/[^\w-]/g, '');
  }
}
